import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Lock, ShieldCheck, CreditCard, Truck, RefreshCw } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { resolveCartDiscount } from "@/lib/discounts";
import PaymentSection from "@/components/checkout/PaymentSection";
import { toast } from "sonner";

const initialForm = {
  full_name: "", company: "", email: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "United States",
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const coupon = location.state?.coupon || null;
  const [form, setForm] = useState({ ...initialForm, email: user?.email || "", full_name: user?.name || "", company: user?.company || "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ruoAcknowledged, setRuoAcknowledged] = useState(false);
  const [rates, setRates] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [localPickup, setLocalPickup] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [paymentCfg, setPaymentCfg] = useState(null);
  const [sale, setSale] = useState(null);
  const [cardToken, setCardToken] = useState("");
  const [tokenizerError, setTokenizerError] = useState("");

  useEffect(() => {
    apiClient.get("/payments/config").then(({ data }) => setPaymentCfg(data)).catch(() => setPaymentCfg({ enabled: false }));
    apiClient.get("/sale/status").then(({ data }) => setSale(data)).catch(() => setSale({ active: false }));
  }, []);

  useEffect(() => {
    if (!paymentCfg?.enabled) return;
    const tokenizerOrigin = new URL(paymentCfg.tokenizer_url).origin;
    const onMessage = (event) => {
      if (event.origin !== tokenizerOrigin) return;
      let payload;
      try {
        payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (payload?.validationError) {
        setCardToken("");
        setTokenizerError(payload.validationError);
        return;
      }
      if (typeof payload?.message === "string" && payload.message.length > 0) {
        setCardToken(payload.message);
        setTokenizerError("");
        setCardError("");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [paymentCfg]);

  const totalQuantity = items.reduce((s, i) => s + i.quantity, 0);
  const addressReady = form.line1.trim() && form.city.trim() && form.state.trim() && form.postal_code.trim().length >= 5;

  const fetchRates = useCallback(async () => {
    setRatesLoading(true);
    setRatesError("");
    try {
      const { data } = await apiClient.post("/shipping/rates", {
        full_name: form.full_name, line1: form.line1, line2: form.line2,
        city: form.city, state: form.state, postal_code: form.postal_code,
        country: form.country, total_quantity: totalQuantity,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      setRates(data.rates);
      if (data.fallback || data.rates.length === 0) {
        setSelectedRate(null);
        setRatesError(data.message || "Live carrier rates are temporarily unavailable.");
        return;
      }
      setSelectedRate((prev) => data.rates.find((r) => r.carrier === prev?.carrier && r.service === prev?.service) || data.rates[0]);
    } catch (e) {
      setRates([]);
      setSelectedRate(null);
      setRatesError(formatApiErrorDetail(e.response?.data?.detail) || "Could not calculate shipping rates.");
    } finally {
      setRatesLoading(false);
    }
  }, [form.full_name, form.line1, form.line2, form.city, form.state, form.postal_code, form.country, totalQuantity]);

  useEffect(() => {
    if (!addressReady) return;
    const t = setTimeout(fetchRates, 800);
    return () => clearTimeout(t);
  }, [addressReady, fetchRates]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, email: f.email || user.email || "", full_name: f.full_name || user.name || "", company: f.company || user.company || "" }));
    }
  }, [user]);

  useEffect(() => {
    const email = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || items.length === 0) return;
    const t = setTimeout(() => {
      apiClient.post("/carts/track", {
        email,
        full_name: form.full_name,
        items: items.map((i) => ({ name: i.name, strength: i.strength, price: i.price, quantity: i.quantity })),
        subtotal,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [form.email, form.full_name, items, subtotal]);

  useEffect(() => {
    const effectiveState = localPickup ? "CA" : form.state;
    if (!effectiveState.trim()) { setTaxRate(0); return; }
    const t = setTimeout(() => {
      apiClient.get("/tax/rate", { params: { state: effectiveState, country: localPickup ? "United States" : form.country } })
        .then((res) => setTaxRate(res.data.rate))
        .catch(() => setTaxRate(0));
    }, 500);
    return () => clearTimeout(t);
  }, [form.state, form.country, localPickup]);

  const { discount, label: discountLabel, useSale, useQtyDiscount } = resolveCartDiscount({ items, subtotal, coupon, sale });
  const freeShipping = Boolean(coupon?.free_shipping);
  const shipping = localPickup ? 0 : freeShipping ? 0 : selectedRate ? selectedRate.amount : (subtotal >= 150 || subtotal === 0 ? 0 : 12);
  const taxAmount = Math.round((subtotal - discount) * taxRate) / 100;
  const total = subtotal - discount + shipping + taxAmount;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const [card, setCard] = useState({ cardName: "" });
  const [cardError, setCardError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0 || !ruoAcknowledged) return;
    if (paymentCfg?.enabled) {
      if (!card.cardName.trim()) {
        setCardError("Cardholder name is required");
        toast.error("Cardholder name is required");
        return;
      }
      if (!cardToken) {
        setCardError("Please complete your card details in the secure payment form");
        toast.error("Please complete your card details in the secure payment form");
        return;
      }
    }
    setCardError("");
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.post("/orders", {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, strength: i.strength,
          batch_number: i.batch_number, price: i.price, quantity: i.quantity,
        })),
        shipping_address: {
          full_name: form.full_name, company: form.company || null, email: form.email, phone: form.phone,
          line1: form.line1, line2: form.line2, city: form.city, state: form.state,
          postal_code: form.postal_code, country: form.country,
        },
        coupon_code: freeShipping ? coupon.code : !useQtyDiscount && !useSale && coupon ? coupon.code : null,
        ruo_acknowledged: ruoAcknowledged,
        shipping_rate_id: localPickup ? null : selectedRate?.rate_id || null,
        local_pickup: localPickup,
        payment: paymentCfg?.enabled ? { token: cardToken } : null,
      });
      clearCart();
      navigate(`/order-confirmation/${data.order_number}`, { state: { email: form.email } });
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      setError(detail);
      toast.error(detail || "Could not place order. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-empty-cart">Your cart is empty.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <h1 className="text-3xl font-display font-medium text-slate-900 mb-10">Checkout</h1>
      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h2 className="font-display font-semibold text-lg text-slate-900 mb-4">Ship To — Researcher / Institution</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Researcher Name</Label><Input name="full_name" required value={form.full_name} onChange={handleChange} data-testid="checkout-fullname-input" /></div>
              <div><Label>Email</Label><Input type="email" name="email" required value={form.email} onChange={handleChange} data-testid="checkout-email-input" /></div>
              <div className="sm:col-span-2"><Label>Company / Institution (optional)</Label><Input name="company" value={form.company} onChange={handleChange} placeholder="Laboratory, university, or research organization" data-testid="checkout-company-input" /></div>
              <div><Label>Phone</Label><Input name="phone" value={form.phone} onChange={handleChange} data-testid="checkout-phone-input" /></div>
              <div><Label>Country</Label><Input name="country" required value={form.country} onChange={handleChange} data-testid="checkout-country-input" /></div>
              <div className="sm:col-span-2"><Label>Address Line 1</Label><Input name="line1" required value={form.line1} onChange={handleChange} data-testid="checkout-line1-input" /></div>
              <div className="sm:col-span-2"><Label>Address Line 2 (optional)</Label><Input name="line2" value={form.line2} onChange={handleChange} data-testid="checkout-line2-input" /></div>
              <div><Label>City</Label><Input name="city" required value={form.city} onChange={handleChange} data-testid="checkout-city-input" /></div>
              <div><Label>State / Province</Label>
                {["us", "usa", "united states", "united states of america"].includes(form.country.trim().toLowerCase()) ? (
                  <select
                    name="state"
                    required
                    value={form.state}
                    onChange={handleChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="checkout-state-input"
                  >
                    <option value="">Select state...</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <Input name="state" required value={form.state} onChange={handleChange} data-testid="checkout-state-input" />
                )}
              </div>
              <div><Label>Postal Code</Label><Input name="postal_code" required value={form.postal_code} onChange={handleChange} data-testid="checkout-postal-input" /></div>
            </div>
          </section>

          <section data-testid="shipping-method-section">
            <h2 className="font-display font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Truck className="h-5 w-5 text-brandblue-600" /> Shipping Method
            </h2>
            <button
              type="button"
              onClick={() => setLocalPickup(!localPickup)}
              className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 text-left mb-3 transition-colors ${localPickup ? "border-brandblue-600 bg-blue-50/50 ring-1 ring-brandblue-600" : "border-slate-200 hover:border-slate-300"}`}
              data-testid="local-pickup-option"
            >
              <span className="flex items-center gap-3">
                <span className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${localPickup ? "border-brandblue-600 bg-brandblue-600" : "border-slate-300"}`} />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Local Pickup — Anaheim, CA</span>
                  <span className="block text-xs text-slate-500">We'll email you to arrange a pickup time after payment</span>
                </span>
              </span>
              <span className="text-sm font-bold text-emerald-600">FREE</span>
            </button>
            {localPickup ? (
              <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-2xl p-4 bg-slate-50" data-testid="local-pickup-note">
                No shipping needed — pick up your order in Anaheim, CA.
              </p>
            ) : !addressReady ? (
              <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-2xl p-5 bg-slate-50" data-testid="shipping-rates-placeholder">
                Enter your shipping address above to see live carrier rates.
              </p>
            ) : ratesLoading ? (
              <div className="space-y-2" data-testid="shipping-rates-loading">
                {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}
              </div>
            ) : ratesError ? (
              <div className="border border-amber-200 bg-amber-50 rounded-2xl p-5" data-testid="shipping-rates-error">
                <p className="text-sm text-amber-800 mb-3">{ratesError} Standard flat-rate shipping will apply.</p>
                <Button type="button" variant="outline" size="sm" onClick={fetchRates} className="rounded-full" data-testid="shipping-rates-retry-btn">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
                </Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="shipping-rates-list">
                {rates.map((r, idx) => (
                  <button
                    type="button"
                    key={r.rate_id}
                    onClick={() => setSelectedRate(r)}
                    className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 text-left transition-colors ${selectedRate?.rate_id === r.rate_id ? "border-brandblue-600 bg-blue-50/50 ring-1 ring-brandblue-600" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`shipping-rate-option-${idx}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${selectedRate?.rate_id === r.rate_id ? "border-brandblue-600 bg-brandblue-600" : "border-slate-300"}`} />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{r.carrier} — {r.service}</span>
                        <span className="block text-xs text-slate-500">{r.estimated_days ? `Est. ${r.estimated_days} business day${r.estimated_days > 1 ? "s" : ""}` : r.duration_terms || ""}</span>
                      </span>
                    </span>
                    <span className="text-sm font-bold text-slate-900">${r.amount.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-display font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-brandblue-600" /> Payment
            </h2>
            <PaymentSection
              paymentCfg={paymentCfg}
              card={card}
              setCard={setCard}
              cardToken={cardToken}
              tokenizerError={tokenizerError}
              cardError={cardError}
            />
          </section>

          <div className="border border-blue-100 bg-blue-50/50 rounded-2xl p-5 text-sm text-slate-600" data-testid="checkout-bulk-inquiry-note">
            <span className="font-semibold text-slate-900">Bulk / Institutional Inquiries:</span> Ordering larger quantities for your laboratory or institution?{" "}
            <Link to="/contact" className="text-brandblue-600 font-semibold hover:text-brandblue-800 transition-colors" data-testid="checkout-bulk-inquiry-link">Contact us</Link> for institutional pricing and documentation support.
          </div>
        </div>

        <div className="border border-slate-200 rounded-2xl p-6 h-fit sticky top-24">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-5">Order Summary</h2>
          <div className="space-y-2 mb-4 max-h-48 overflow-y-auto scrollbar-thin">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm text-slate-600">
                <span>{i.name} x{i.quantity}</span><span>${(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-slate-100 pt-4">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-600"><span>{discountLabel}</span><span data-testid="checkout-discount">-${discount.toFixed(2)}</span></div>}
            {taxAmount > 0 && <div className="flex justify-between text-slate-600"><span>Sales Tax ({taxRate.toFixed(0)}%)</span><span data-testid="checkout-tax">${taxAmount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-slate-600">
              <span>Shipping{selectedRate ? ` (${selectedRate.carrier} ${selectedRate.service})` : ""}{freeShipping ? ` — FREE with ${coupon.code}` : ""}</span>
              <span data-testid="checkout-shipping-cost" className={freeShipping ? "text-emerald-600 font-semibold" : ""}>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between font-display font-bold text-slate-900 text-base pt-3 border-t border-slate-100"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
          </div>

          {error && <p className="text-xs text-red-500 mt-3" data-testid="checkout-error">{error}</p>}

          <div className="flex items-start gap-3 mt-5">
            <Checkbox
              id="checkout-ruo-checkbox"
              checked={ruoAcknowledged}
              onCheckedChange={(v) => setRuoAcknowledged(!!v)}
              className="mt-0.5"
              data-testid="checkout-ruo-checkbox"
            />
            <label htmlFor="checkout-ruo-checkbox" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
              I certify that I am at least twenty-one (21) years of age, understand these products are sold strictly for Research Use Only, are not intended for human consumption, and I have read and agree to the{" "}
              <Link to="/research-use-disclaimer" target="_blank" rel="noopener noreferrer" className="text-brandblue-600 font-semibold hover:text-brandblue-800 transition-colors" data-testid="checkout-ruo-disclaimer-link">
                Research Use Only Disclaimer
              </Link>{" "}
              and{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-brandblue-600 font-semibold hover:text-brandblue-800 transition-colors" data-testid="checkout-terms-link">
                Terms &amp; Conditions
              </Link>
              .
            </label>
          </div>

          <Button type="submit" size="lg" disabled={loading || !ruoAcknowledged} className="w-full mt-4 rounded-full btn-lift" data-testid="place-order-btn">
            <Lock className="h-4 w-4 mr-2" /> {loading ? "Placing Order..." : "Place Order"}
          </Button>
          <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Encrypted checkout. Research Use Only compliance required.</p>
        </div>
      </form>
    </div>
  );
}
